import { useCallback, useEffect, useState } from "react";
import { adminFetch } from "@/utils/adminAuth";
import type { AgentMetricsSnapshot, MetricsWindow } from "../utils/types";

export const useAdminMetrics = (window: MetricsWindow, enabled = true) => {
  const [metrics, setMetrics] = useState<AgentMetricsSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;

    try {
      const response = await adminFetch(`/api/admin/metrics?window=${window}`);

      if (!response.ok) throw new Error(`Metrics request failed (${response.status})`);

      const body = await response.json() as { metrics: AgentMetricsSnapshot };
      setMetrics(body.metrics);
      setError(null);
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : "Failed to load metrics.");
    } finally {
      setLoading(false);
    }
  }, [enabled, window]);

  useEffect(() => {
    if (!enabled) return;

    setLoading(true);
    void refresh();
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(() => {
      void refresh();
    }, 30_000);

    return () => clearInterval(interval);
  }, [enabled, refresh]);

  return { metrics, loading, error, refresh };
};
